import { useCallback, useState, useMemo } from 'react'
import styles from './styles/select.module.scss'
import cx from 'classnames'
import Separator from './Separator'
import Select, {
  GroupBase,
  OptionProps,
  SingleValueProps,
  StylesConfig,
  components,
} from 'react-select'
import { ClassNamesProps } from 'types/utilities'

const customStyles: StylesConfig<any, false, GroupBase<any>> = {
  indicatorsContainer: (provided, state) => ({
    ...provided,
    display: 'none',
  }),
  input: (provided, state) => ({
    ...provided,
    margin: 0,
    padding: 0,
    height: '26px',
    color: '#16234c',
    backgroundColor: 'transparent',
  }),
  menu: (provided, state) => ({
    ...provided,
    zIndex: 2,
    marginTop: '4px',
    paddingTop: '6px',
    paddingBottom: '6px',
    borderRadius: '8px',
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    padding: '13px 40px 13px 24px',
    backgroundColor: 'transparent',
  }),
  option: (provided, state) => ({
    ...provided,
    cursor: 'pointer',
    paddingLeft: '24px',
    height: '26px',
    minHeight: 'fit-content',
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    fontSize: '1rem',
    backgroundColor: state.isSelected ? '#003a70' : state.isFocused ? '#f6f7f9' : '#fff',
    '&:hover': {
      color: '#16234c',
      backgroundColor: '#f6f7f9',
    },
    '&:focus': {
      color: '#16234c',
      backgroundColor: '#f6f7f9',
    },
  }),
  control: (_, state) => ({
    cursor: state.isDisabled ? 'unset' : 'pointer',
    width: '100%',
    maxWidth: '100%',
    height: '52px',
    transition: 'all 0.6s ease-in, background-image 0.1s ease-out',
    textOverflow: 'ellipsis',
    color: state.isDisabled ? '#9da1af' : '#16234c',
    borderRadius: '8px',
    border: state.isFocused ? 'solid 1px #003a70' : 'solid 1px #dadce0',
    fontWeight: 'normal',
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: '1.6',
    letterSpacing: '0.32px',
    textAlign: 'left',
    fontSize: '1rem',
    backgroundColor: state.isDisabled ? '#f6f7f9' : state.isFocused ? '#f2f5f8' : '#ffffff',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '24px',
    backgroundPosition: 'center right 6px',
    '&:hover': {
      border: !state.isFocused ? 'solid 1px #8f99b7' : 'solid 1px #003a70',
    },
  }),
}

export interface Option {
  value: string
  label: string
}

type ClassKeys = 'main' | 'clearBtn' | 'separator' | 'option'

interface SimpleSelectProps extends ClassNamesProps<ClassKeys> {
  value: string
  options: Option[]
  placeholder?: string
  disabled?: boolean
  disableErrorMessage?: boolean
  onValueChange: (value: string) => void
  withClearOption?: boolean
  size?: 'small' | 'medium'
  makeTriggerLabel?: (selectedOption: Option) => string
}

export const SimpleSelect = ({
  value,
  options,
  classNames,
  placeholder = '',
  onValueChange,
  size = 'medium',
  withClearOption = false,
  makeTriggerLabel,
}: SimpleSelectProps) => {
  const [isOpen, setIsOpen] = useState(false)

  const CustomOption = (props: OptionProps<Option, false>) => {
    if (!props.data.value && withClearOption)
      return (
        <>
          <components.Option
            {...props}
            className={cx(
              props.className,
              classNames?.option,
              classNames?.clearBtn,
              styles.clearBtn
            )}
          />
          <Separator className={styles.separator} />
        </>
      )
    return <components.Option {...props} className={cx(props.className, classNames?.option)} />
  }

  const SingleValue = useMemo(() => {
    if (makeTriggerLabel) {
      return (props: SingleValueProps<Option, false>) => (
        <components.SingleValue {...props}>{makeTriggerLabel(props.data)}</components.SingleValue>
      )
    }
    return (props: SingleValueProps<Option, false>) => <components.SingleValue {...props} />
  }, [makeTriggerLabel])

  const opts =
    value && withClearOption ? [{ label: 'Clear filter', value: '' }, ...options] : options

  const handleClose = useCallback(() => {
    document
      .querySelector('.simpleSelect-wtn__menu')
      ?.classList.add('simpleSelect-wtn__menu--is-closing')
    setTimeout(() => {
      setIsOpen(false)
    }, 150)
  }, [])

  const handleOpen = useCallback(() => {
    setIsOpen(true)
  }, [])

  return (
    //@ts-ignore
    <Select
      menuIsOpen={isOpen}
      onMenuOpen={handleOpen}
      onMenuClose={handleClose}
      components={{ Option: CustomOption, SingleValue }}
      isSearchable={false}
      options={opts}
      value={value ? opts.find(option => option.value === value) : null}
      onChange={(option: Option | null) => {
        onValueChange(option?.value ?? '')
      }}
      placeholder={placeholder}
      className={cx(styles.select, classNames?.main, styles[size])}
      classNamePrefix='simpleSelect-wtn'
      styles={customStyles}
    />
  )
}

export default SimpleSelect
